// Command nodes — the places she is told to watch, and how hard.
//
// A country dossier is background. A command node is a standing order: watch
// this place, at this threat level, for these reasons, and tell me when it
// changes. You can drop one from the globe or ask her in chat ("watch Dublin
// port at threat 4 for drone activity") and it lands here, on the map, in her
// awareness, and in the military tracking rollup.
//
// Everything is local and persistent. Threat levels are hers to raise on
// evidence — she logs why every time she moves one.

import { getOption, setOption } from "./persistOptions";
import { ATLAS } from "./seed-atlas";
import type { MapNode } from "./mapFusion";

/** 1 = noted, 5 = active hostile. */
export type ThreatLevel = 1 | 2 | 3 | 4 | 5;

export type WatchClass = "civil" | "military" | "infrastructure" | "border" | "orbital" | "cyber" | "humanitarian";

export interface CommandNode {
  id: string;
  label: string;
  lat: number;
  lng: number;
  threat: ThreatLevel;
  cls: WatchClass;
  /** Why it is being watched, in your words or hers. */
  brief: string;
  /** Her running log of level changes and live reads. */
  log: Array<{ at: number; text: string; threat: ThreatLevel }>;
  createdAt: number;
  updatedAt: number;
  /** Watch harder than the rest: doubles the polling weight and alert priority. */
  priority: boolean;
  /** Ring around the node, in km. Crossing into it raises a geofence alert. */
  geofenceKm?: number;

}

const KEY = "command:nodes";
const listeners = new Set<() => void>();

export const THREAT_META: Record<ThreatLevel, { label: string; colour: string; note: string }> = {
  1: { label: "Noted", colour: "#7dd3fc", note: "On the board, nothing moving." },
  2: { label: "Watch", colour: "#a3e635", note: "Worth a look each pass." },
  3: { label: "Elevated", colour: "#fbbf24", note: "Something has changed and not settled." },
  4: { label: "High", colour: "#fb923c", note: "Active risk to people on the ground." },
  5: { label: "Critical", colour: "#fb7185", note: "Hostile activity — treat every read as urgent." },
};

export const WATCH_CLASS_META: Record<WatchClass, { label: string; emoji: string }> = {
  civil: { label: "Civil", emoji: "🏙" },
  military: { label: "Military", emoji: "🛡" },
  infrastructure: { label: "Infrastructure", emoji: "⚙" },
  border: { label: "Border", emoji: "🚧" },
  orbital: { label: "Orbital", emoji: "🛰" },
  cyber: { label: "Cyber", emoji: "🕸" },
  humanitarian: { label: "Humanitarian", emoji: "🤝" },
};

function emit(): void { for (const fn of listeners) { try { fn(); } catch { /* ignore */ } } }

export function subscribeCommandNodes(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function commandNodes(): CommandNode[] {
  const raw = getOption<CommandNode[]>(KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((n) => n && typeof n.lat === "number" && typeof n.lng === "number")
    .map((n) => ({ ...n, log: Array.isArray(n.log) ? n.log : [] }))
    .sort((a, b) => b.threat - a.threat || b.updatedAt - a.updatedAt);
}

function save(list: CommandNode[]): void {
  setOption(KEY, list.slice(0, 400));
  emit();
}

export function addCommandNode(input: {
  label: string;
  lat: number;
  lng: number;
  threat?: ThreatLevel;
  cls?: WatchClass;
  brief?: string;
  priority?: boolean;
  geofenceKm?: number;
}): CommandNode {
  const now = Date.now();
  const node: CommandNode = {
    id: `cmd:${now.toString(36)}:${Math.random().toString(36).slice(2, 6)}`,
    label: input.label.trim() || "Unnamed watch",
    lat: input.lat,
    lng: input.lng,
    threat: input.threat ?? 2,
    cls: input.cls ?? "civil",
    brief: input.brief?.trim() || "Standing watch — no specific reason given yet.",
    log: [{ at: now, text: "Watch opened.", threat: input.threat ?? 2 }],
    createdAt: now,
    updatedAt: now,
    priority: input.priority ?? false,
    geofenceKm: input.geofenceKm ?? 0,
  };

  save([node, ...commandNodes()]);
  return node;
}

export function updateCommandNode(id: string, patch: Partial<CommandNode>, why?: string): CommandNode | null {
  const list = commandNodes();
  const i = list.findIndex((n) => n.id === id);
  if (i < 0) return null;
  const before = list[i]!;
  const next: CommandNode = { ...before, ...patch, id: before.id, updatedAt: Date.now() };
  if (patch.threat && patch.threat !== before.threat) {
    next.log = [
      { at: Date.now(), text: why ?? `Threat moved ${before.threat} → ${patch.threat}.`, threat: patch.threat },
      ...before.log,
    ].slice(0, 40);
  } else if (why) {
    next.log = [{ at: Date.now(), text: why, threat: next.threat }, ...before.log].slice(0, 40);
  }
  list[i] = next;
  save(list);
  return next;
}

export function removeCommandNode(id: string): void {
  save(commandNodes().filter((n) => n.id !== id));
}

export function noteOnCommandNode(id: string, text: string): void {
  updateCommandNode(id, {}, text);
}

/* ------------------------------------------------------------ chat control */

const CLASS_WORDS: Array<[RegExp, WatchClass]> = [
  [/\bmilitar|troop|base|garrison|navy|air ?force|missile\b/i, "military"],
  [/\bgrid|power|pipeline|cable|port|dam|reactor|rail\b/i, "infrastructure"],
  [/\bborder|crossing|frontier|checkpoint\b/i, "border"],
  [/\borbit|satellite|launch|spaceport\b/i, "orbital"],
  [/\bcyber|network|ddos|intrusion|hack\b/i, "cyber"],
  [/\baid|refugee|famine|camp|shelter|flood|quake\b/i, "humanitarian"],
];

function classify(text: string): WatchClass {
  for (const [re, cls] of CLASS_WORDS) if (re.test(text)) return cls;
  return "civil";
}

function findPlace(text: string): { label: string; lat: number; lng: number } | null {
  const t = text.toLowerCase();
  // Longest name first, so "South Africa" beats "Africa"-ish partial hits.
  const sorted = [...ATLAS].sort((a, b) => b.name.length - a.name.length);
  for (const c of sorted) {
    if (t.includes(c.name.toLowerCase())) return { label: c.name, lat: c.lat, lng: c.lng };
  }
  const coord = text.match(/(-?\d{1,2}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)/);
  if (coord) {
    const lat = Number(coord[1]);
    const lng = Number(coord[2]);
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { label: `${lat.toFixed(3)}, ${lng.toFixed(3)}`, lat, lng };
    }
  }
  return null;
}

export interface WatchCommand {
  ok: boolean;
  node?: CommandNode;
  reply: string;
}

/**
 * Read a plain sentence and open a watch from it.
 * "watch Ukraine at threat 4 for troop movement" · "watch 53.34, -6.26 priority".
 */
export function watchFromText(text: string): WatchCommand {
  const body = text.replace(/^\s*\/?watch\b/i, "").trim();
  if (!body) {
    return { ok: false, reply: "Tell me where. `/watch Ukraine at threat 4 for troop movement`, or a latitude and longitude." };
  }
  const place = findPlace(body);
  if (!place) {
    return {
      ok: false,
      reply: `I can't place "${body}" yet. Give me coordinates like 53.34, -6.26, or a country I hold a dossier on, and the watch goes straight onto the globe.`,
    };
  }
  const lvl = body.match(/\b(?:threat|level)\s*([1-5])\b/i) ?? body.match(/\bt([1-5])\b/i);
  const threat = (lvl ? Number(lvl[1]) : 3) as ThreatLevel;
  const reason = body.match(/\b(?:for|because|re)\s+(.+)$/i)?.[1]?.trim();
  const node = addCommandNode({
    label: place.label,
    lat: place.lat,
    lng: place.lng,
    threat,
    cls: classify(body),
    brief: reason ?? "Standing watch, opened from chat.",
    priority: /\bpriorit|hard|closer than|more than others\b/i.test(body),
  });
  return {
    ok: true,
    node,
    reply: `Watching ${node.label} at threat ${node.threat} — ${THREAT_META[node.threat].label.toLowerCase()}. ${
      WATCH_CLASS_META[node.cls].label
    } focus${node.priority ? ", priority polling" : ""}. ${node.brief} It's on the globe now and in every sweep I run.`,
  };
}

/* ---------------------------------------------------------------- rollups */

export function commandNodeMapNodes(): Array<MapNode & { detail: string; threat: ThreatLevel }> {
  return commandNodes().map((n) => ({
    id: n.id,
    label: `${WATCH_CLASS_META[n.cls].emoji} ${n.label}`,
    lat: n.lat,
    lng: n.lng,
    kind: n.threat >= 4 ? "danger" : n.threat === 3 ? "caution" : "pin",
    weight: 2 + n.threat + (n.priority ? 2 : 0),
    threat: n.threat,
    detail: [
      `Command watch · ${WATCH_CLASS_META[n.cls].label} · threat ${n.threat} (${THREAT_META[n.threat].label}).`,
      THREAT_META[n.threat].note,
      n.brief,
      n.priority ? "Priority: polled twice as often as the rest of the board." : "",
      n.log.length ? `Last entry: ${new Date(n.log[0]!.at).toLocaleString()} — ${n.log[0]!.text}` : "",
    ].filter(Boolean).join("\n"),
  }));
}

export function threatBoard(): { total: number; byLevel: Record<ThreatLevel, number>; highest: CommandNode | null } {
  const list = commandNodes();
  const byLevel: Record<ThreatLevel, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const n of list) byLevel[n.threat] += 1;
  return { total: list.length, byLevel, highest: list[0] ?? null };
}

export function commandLine(): string {
  const b = threatBoard();
  if (!b.total) return "No command nodes yet. Ask me to watch somewhere and it becomes a standing order.";
  const hot = b.byLevel[4] + b.byLevel[5];
  return `${b.total} command node${b.total === 1 ? "" : "s"} on the board${
    hot ? `, ${hot} at high or critical` : ", all quiet"
  }. Highest: ${b.highest?.label} at threat ${b.highest?.threat}.`;
}
